import { useMemo } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { getNurseShiftOccurrencesByWeek, sortNurses } from "../../utils/autoStaffing";
import { isNurseActiveInDepartment } from "../../utils/formatters";
import type { EmploymentType, HospitalData, Nurse, NurseFullRole, NurseInShift } from "../../utils/models";
import { inverseRolesMap } from "../../utils/utilFunctions";
import { type AddNurseCandidateCriteria, isAddNurseCandidate } from "./addNurseCandidates";

type Args = {
	weekIndex: number;
	dayNo: number;
	shiftNo: number;
	/** Roles the list may show — the role picked in step 1 (role mode) or every role the caller displays. */
	allowedRoles: readonly NurseFullRole[];
	/** Only staff holding this job (the step-1 job, or the request's job); `undefined` = no job filter. */
	jobId: string | undefined;
	contractTypes: readonly EmploymentType[];
	/** Limit to staff who can run the shift as charge (an `isCharged` caller). */
	chargeOnly: boolean;
	showActual: boolean;
	nurseIdsToIgnore: readonly number[] | undefined;
};

type RankArgs = Pick<Args, "weekIndex" | "dayNo" | "shiftNo" | "showActual"> & {
	hospitalData: HospitalData;
	role: NurseFullRole;
	nurseOccurrences: Record<number, number>;
	departmentNurseTypes: NurseFullRole[];
	calculateShiftTimes: Parameters<typeof sortNurses>[5];
};

/**
 * One role's candidates, best first. Actuals list everyone of the role (who is already punched into the
 * slot is dropped by the shared candidate filter); the plan ranks by `sortNurses` and then appends the rest
 * of the role, so nobody disappears from the list — the availability resolver marks who can't take the
 * shift instead of hiding them.
 */
const rankRoleCandidates = ({
	hospitalData,
	role,
	nurseOccurrences,
	departmentNurseTypes,
	calculateShiftTimes,
	weekIndex,
	dayNo,
	shiftNo,
	showActual,
}: RankArgs) => {
	if (showActual) {
		return hospitalData.nurses.filter((nurse) => nurse.role === role && departmentNurseTypes.includes(role));
	}
	const ranked = sortNurses(hospitalData, shiftNo, weekIndex, dayNo, nurseOccurrences, calculateShiftTimes, false, inverseRolesMap[role]);
	const rankedSet = new Set(ranked);
	return [...ranked, ...hospitalData.nurses.filter((nurse) => nurse.role === role && !rankedSet.has(nurse))];
};

/**
 * Who the list offers for the shift, in suggestion order (department role priority, then `sortNurses`
 * within a role). Returns the week's shift occurrences too — the availability resolver reads them.
 */
export const useAddStaffCandidates = (args: Args) => {
	const { weekIndex, dayNo, shiftNo, allowedRoles, jobId, contractTypes, chargeOnly, showActual, nurseIdsToIgnore } = args;
	const { hospitalData, chosenDepartment, calculateShiftTimes, departmentNurseTypes } = useHospitalData();

	const nurseOccurrences = useMemo(() => getNurseShiftOccurrencesByWeek(hospitalData, weekIndex), [hospitalData, weekIndex]);

	const orderedCandidates = useMemo<Nurse[]>(
		() =>
			departmentNurseTypes
				.filter((role) => allowedRoles.includes(role))
				.flatMap((role) =>
					rankRoleCandidates({ hospitalData, role, nurseOccurrences, departmentNurseTypes, calculateShiftTimes, weekIndex, dayNo, shiftNo, showActual }),
				),
		[departmentNurseTypes, allowedRoles, hospitalData, nurseOccurrences, calculateShiftTimes, weekIndex, dayNo, shiftNo, showActual],
	);

	// Only the actual view leaves out who is already recorded on the slot; the plan lists them so the
	// availability resolver can show "Already on …" in the can't-work group.
	const nurseIdsOnShift = useMemo<ReadonlySet<number>>(
		() =>
			showActual
				? new Set(hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo].nursesActual?.map((nurse: NurseInShift) => nurse.nurseId))
				: new Set<number>(),
		[showActual, hospitalData, weekIndex, shiftNo, dayNo],
	);

	const candidates = useMemo<Nurse[]>(() => {
		const criteria: AddNurseCandidateCriteria = {
			roles: allowedRoles,
			contractTypes,
			// The active-in-department gate has only ever applied to the plan; actuals mode keeps listing everyone.
			activeInDepartmentId: showActual ? null : chosenDepartment.department_id,
			// Not filtered here: someone leaving before the shift is shown in the can't-work group ("Leaves on …").
			deactivationCutoff: null,
			chargeOnly,
			nurseIdsToIgnore,
			requiredJobId: jobId,
			nurseIdsOnShift,
		};
		return orderedCandidates.filter((nurse) => isAddNurseCandidate(nurse, criteria));
	}, [orderedCandidates, allowedRoles, contractTypes, showActual, chosenDepartment.department_id, chargeOnly, nurseIdsToIgnore, jobId, nurseIdsOnShift]);

	// Does anyone in the department hold the step-1 job / role at all? Deliberately ignores the contract
	// filter, charge, the ignore list and "already in this actual shift", so an empty list can tell "nobody
	// here has this job" apart from "nobody matches your search or filters".
	const hasAnyHolder = useMemo(() => {
		const roles = new Set(departmentNurseTypes.filter((role) => allowedRoles.includes(role)));
		return hospitalData.nurses.some(
			(nurse) =>
				roles.has(nurse.role) &&
				(showActual || isNurseActiveInDepartment(nurse, chosenDepartment.department_id)) &&
				(!jobId || !!nurse.jobIds?.includes(jobId)),
		);
	}, [departmentNurseTypes, allowedRoles, hospitalData.nurses, showActual, chosenDepartment.department_id, jobId]);

	return { candidates, nurseOccurrences, hasAnyHolder };
};
